import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import AutoSizer from 'react-virtualized-auto-sizer';
import {roundDecimal} from '~/shared/utils/math';
import Color from '~/data/records/Color';
import {iconPropType} from '~/data/records/icons';
// constants and utils
import MediaQueries, {breakpoint} from '~/constants/MediaQueries';
import {
  PRODUCT_TRAY_HEIGHT,
  SANDWICH_BOTTOM_HEIGHT,
  STANDARD_PRODUCT_IMAGE_WIDTH
} from '~/constants/CanvasConstants';
import mapProperties from '~/utils/mapProperties';
import {getColor, isMobile} from '~/utils/catalog';
// components
import Product from '~/components/Product';
import Design from '~/components/Design';
import DesignContextMenu from '~/components/DesignContextMenu';
// vendor
import DesignIconConstants from '~/constants/DesignIconConstants';

import './index.scss';

const propTypes = {
  currentColorId: PropTypes.string.isRequired,
  icons: PropTypes.arrayOf(iconPropType).isRequired,
  product: PropTypes.shape({
    colors: PropTypes.array.isRequired,
    id: PropTypes.string.isRequired,
    style_type: PropTypes.string.isRequired,
    views: PropTypes.array.isRequired
  }).isRequired,
  activateIntersectingIconButton: PropTypes.func,
  backward: PropTypes.func,
  blankDesign: PropTypes.bool,
  blur: PropTypes.bool,
  center: PropTypes.func,
  className: PropTypes.string,
  constraint: PropTypes.shape({
    height: PropTypes.number.isRequired,
    left: PropTypes.number.isRequired,
    top: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired
  }),
  copy: PropTypes.func,
  cut: PropTypes.func,
  deactivateIntersectingIconButton: PropTypes.func,
  disabled: PropTypes.bool,
  duplicate: PropTypes.func,
  fetchingDesign: PropTypes.bool,
  forward: PropTypes.func,
  guidelines: PropTypes.arrayOf(
    PropTypes.shape({
      height: PropTypes.number.isRequired,
      left: PropTypes.number.isRequired,
      top: PropTypes.number.isRequired,
      width: PropTypes.number.isRequired,
      name: PropTypes.string
    })
  ),
  handleAfterOffClick: PropTypes.func,
  handleInvalidProductImage: PropTypes.func,
  handleOpenIconTools: PropTypes.func,
  handleSelectIcon: PropTypes.func,
  // event handlers
  handleSelectIcons: PropTypes.func,
  hasClipping: PropTypes.bool,
  iconRendered: PropTypes.func,
  iconsInView: PropTypes.array,
  interactive: PropTypes.bool,
  intersectingIcons: PropTypes.object,
  isCircularEmbroideryTestEnabled: PropTypes.bool,
  isSleeveDecoEligible: PropTypes.bool,
  isZoomed: PropTypes.bool,
  largeBreakPoint: PropTypes.bool,
  main: PropTypes.bool,
  move: PropTypes.func,
  navigateToCrop: PropTypes.func,
  paste: PropTypes.func,
  productColor: PropTypes.instanceOf(Color),
  remove: PropTypes.func,
  repositionForTextEffects: PropTypes.func,
  resize: PropTypes.func,
  selectAll: PropTypes.func,
  selectedIconId: PropTypes.string,
  selectedIconIds: PropTypes.arrayOf(PropTypes.string),
  selectedIconType: PropTypes.string,
  selectedView: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    printScaleFactor: PropTypes.number.isRequired,
    constraint: PropTypes.shape({
      height: PropTypes.number.isRequired,
      left: PropTypes.number.isRequired,
      top: PropTypes.number.isRequired,
      width: PropTypes.number.isRequired
    })
  }),
  showOverlay: PropTypes.bool,
  similarProductWithImages: PropTypes.func,
  toggleView: PropTypes.func,
  updateFontSize: PropTypes.func,
  onClick: PropTypes.func
};

/** @extends {Component<PropTypes.InferProps<typeof propTypes>>} */
export default class Canvas extends Component {
  static displayName = 'Canvas';
  static propTypes = propTypes;

  static defaultProps = {
    showGuidelines: false,
    icons: [],
    handleAfterOffClick() {},
    onClick() {}
  };

  state = {
    productImageDimensions: {
      height: 200,
      width: 200
    },
    isContextMenuOpen: false,
    contextMenuPosition: null
  };

  zoomContainerNode = null;

  updateProductImage = imageDimensions => {
    this.setState({
      productImageDimensions: imageDimensions
    });
  };

  zoomMargins = () => {
    if (breakpoint(MediaQueries.largeAndAbove)) {
      return {
        height: PRODUCT_TRAY_HEIGHT,
        width: 16
      };
    } else {
      return {
        height: SANDWICH_BOTTOM_HEIGHT,
        width: 16
      };
    }
  };

  buildZoomedStyles = (zoomConstraint, height, width) => ({
    left: -(zoomConstraint.left + 0.5 * zoomConstraint.width),
    top: -(zoomConstraint.top + 0.5 * zoomConstraint.height),
    transform: `translate(${width / 2 - this.zoomMargins().width}px, ${
      height / 2 - this.zoomMargins().height / 2
    }px)`
  });

  handleOffClick = e => {
    this.props.onClick(e);

    if (!e.defaultPrevented) {
      this.props.handleAfterOffClick(e);
    }
  };

  buildZoomFactor = (height, width) => {
    const {productImageDimensions} = this.state;
    const {printScaleFactor} = this.props.selectedView;
    const {constraint} = this.props;
    const heightScale =
      (height - this.zoomMargins().height) /
      (constraint.height *
        (printScaleFactor *
          ((productImageDimensions.width *
            (height / productImageDimensions.height)) /
            STANDARD_PRODUCT_IMAGE_WIDTH)));
    const widthScale =
      (width - this.zoomMargins().width * 2) /
      (constraint.width *
        (printScaleFactor *
          ((productImageDimensions.width *
            (height / productImageDimensions.width)) /
            STANDARD_PRODUCT_IMAGE_WIDTH)));
    const largestScale = Math.min(heightScale, widthScale);
    return roundDecimal(Math.max(largestScale, 1));
  };

  getZoomFactor = (screenWidth, screenHeight) => {
    return this.props.isZoomed
      ? this.buildZoomFactor(screenHeight, screenWidth)
      : 1;
  };

  getViewportScale = computedHeight => {
    const {height: actualImageHeight} = this.state.productImageDimensions;
    const viewportScale = roundDecimal(computedHeight / actualImageHeight, 4);
    return viewportScale;
  };

  getActualDimensions = computedHeight => {
    const {productImageDimensions} = this.state;

    // get ratio of computedHeight / natural height,
    // so we know how big the image is as we display it.
    const viewportScale = this.getViewportScale(computedHeight);

    const actualImageDimensions = mapProperties(
      productImageDimensions,
      p => p * viewportScale
    );
    return actualImageDimensions;
  };

  getStandardRatio = actualImageDimensions => {
    return actualImageDimensions.width / STANDARD_PRODUCT_IMAGE_WIDTH;
  };

  getModifiedPixelScaleFactor = standardRatio => {
    const {
      selectedView: {printScaleFactor}
    } = this.props;
    return roundDecimal(printScaleFactor * standardRatio, 1) || 1;
  };

  isContraintBeyondBorders = (screenWidth, screenHeight) => {
    // scale natural image size to actual
    const actualImageDimensions = this.getActualDimensions(screenHeight);

    // calculate the ratio of the 'standard image size' 513/490 to the actual dimensions
    const standardRatio = this.getStandardRatio(actualImageDimensions);

    // apply standard ratio to print scale factor
    const modifiedPixelScaleFactor =
      this.getModifiedPixelScaleFactor(standardRatio);

    // check if constraint if off the screen
    const isContraintBeyondBorders =
      this.props.constraint.width * modifiedPixelScaleFactor >= screenWidth;

    return isContraintBeyondBorders;
  };

  /** @param {React.ChangeEvent<HTMLInputElement>} ev */
  openContextMenu = ev => {
    ev.preventDefault();
    ev.stopPropagation();

    const {left, top} = this.zoomContainerNode.getBoundingClientRect();
    const position = {
      left: ev.clientX - left,
      top: ev.clientY - top
    };

    this.setState({
      isContextMenuOpen: true,
      contextMenuPosition: position
    });
  };

  closeContextMenu = () => {
    this.setState({
      isContextMenuOpen: false,
      contextMenuPosition: null
    });
  };

  handleContextMenuCut = () => {
    this.props.cut();
    this.props.handleAfterOffClick();
  };

  uploadCropEnabled = () =>
    this.props.selectedIconIds?.length === 1 &&
    this.props?.selectedIconType === DesignIconConstants.TYPES.upload;

  renderContextmenu = () => {
    return (
      this.state.isContextMenuOpen && (
        <DesignContextMenu
          backward={this.props.backward}
          center={this.props.center}
          copy={this.props.copy}
          cut={this.handleContextMenuCut}
          forward={this.props.forward}
          hasClipping={this.props.hasClipping}
          paste={this.props.paste}
          position={this.state.contextMenuPosition}
          remove={this.props.remove}
          selectAll={this.props.selectAll}
          selectedIconIds={this.props.selectedIconIds}
          onClose={this.closeContextMenu}
          crop={this.props.navigateToCrop}
          uploadCropEnabled={this.uploadCropEnabled}
        />
      )
    );
  };

  render() {
    const classes = classnames(
      'ndx-Canvas',
      {
        'ndx-Canvas--disabled': this.props.disabled,
        'ndx-Canvas--blurred': this.props.blur
      },
      this.props.className
    );
    const prevent = e => e?.preventDefault?.();

    return (
      <div
        className={classes}
        onContextMenu={this.openContextMenu}
        onDragStartCapture={prevent}
        onMouseDown={this.handleOffClick}
        onTouchMove={prevent}>
        <AutoSizer>
          {({height, width}) => {
            const {productImageDimensions} = this.state;
            const {width: actualImageWidth, height: actualImageHeight} =
              productImageDimensions;
            const {constraint, isZoomed} = this.props;

            const zoomFactor = this.getZoomFactor(width, height);

            /*
             * Some of the non-apparel products' constraints go off the canvas on mobile screens
             *
             * Issues and solutions:
             * 1) If the constraints are located in the right or left side of a product image
             * then, first place the constraint in the center of the screen
             * and shift product image left or right accordingly in that case
             *
             * 2) If the constraint's width is bigger than the screen's width,
             * then assign computed height to the actual height of the image.
             *
             */

            const isNonApparel =
              isMobile() && this.props.product?.style_type === 'non-apparel';
            const isContraintBeyondBorders = isNonApparel
              ? this.isContraintBeyondBorders(width, height)
              : false;

            /*
             * Now, calculate the actual dimensions of the constraints, guidelines, and image
             */

            const computedHeight = isContraintBeyondBorders
              ? actualImageWidth
              : height * zoomFactor;
            const computedWidth = width * zoomFactor;

            // scale natural image size to actual
            const actualImageDimensions =
              this.getActualDimensions(computedHeight);

            // calculate the ratio of the 'standard image size' 513/490 to the actual dimensions
            const standardRatio = this.getStandardRatio(actualImageDimensions);

            // apply standard ratio to print scale factor
            const modifiedPixelScaleFactor =
              this.getModifiedPixelScaleFactor(standardRatio);

            // calculate where to place product within the container
            const constraintWidth = constraint.width * modifiedPixelScaleFactor;
            const constraintLeft = constraint.left * standardRatio;

            const top = isContraintBeyondBorders
              ? (height - actualImageHeight) / 2
              : 0;
            const left = isNonApparel
              ? computedWidth / 2 - constraintWidth / 2 - constraintLeft
              : (computedWidth - actualImageDimensions.width) / 2;

            const productAnchor = {top, left};

            // calculate the design area in pixels.
            const buildFromDimensions = ({top, left, width, height, name}) => {
              const newObj = {
                top: top * standardRatio + productAnchor.top,
                left: left * standardRatio + productAnchor.left,
                width: width * modifiedPixelScaleFactor,
                height: height * modifiedPixelScaleFactor
              };
              name ? (newObj.name = name) : null;
              return newObj;
            };

            const guidelines = this.props.main
              ? this.props.guidelines.map(buildFromDimensions)
              : undefined;

            const zoomConstraint = buildFromDimensions(constraint);

            const handleIconRender = iconInfo => {
              if (this.props.iconRendered) {
                this.props.iconRendered({
                  ...iconInfo,
                  viewportScale: modifiedPixelScaleFactor
                });
              }
            };

            const similarProduct = this.props.similarProductWithImages?.(
              this.props.product?.id,
              this.props.productColor?.id
            );

            return (
              !this.props.fetchingDesign && (
                <div
                  ref={node => {
                    this.zoomContainerNode = node;
                  }}
                  className="ndx-Canvas-zoomContainer"
                  style={
                    isZoomed && zoomFactor > 1
                      ? this.buildZoomedStyles(zoomConstraint, height, width)
                      : {}
                  }>
                  <Product
                    anchorPoint={productAnchor}
                    handleInvalidProductImage={
                      this.props.handleInvalidProductImage
                    }
                    height={computedHeight}
                    isSleeveDecoEligible={this.props.isSleeveDecoEligible}
                    main={this.props.main}
                    product={this.props.product}
                    selectedColorDefinition={getColor(
                      this.props.product,
                      this.props.currentColorId
                    )}
                    selectedViewName={this.props.selectedView.name}
                    showOverlay={this.props.showOverlay}
                    styleId={this.props.product?.id}
                    similarProductWithImages={similarProduct}
                    onProductRender={this.updateProductImage}>
                    <Design
                      activateIntersectingIconButton={
                        this.props.activateIntersectingIconButton
                      }
                      deactivateIntersectingIconButton={
                        this.props.deactivateIntersectingIconButton
                      }
                      duplicate={this.props.duplicate}
                      intersectingIcons={this.props.intersectingIcons}
                      isCircularEmbroideryTestEnabled={
                        this.props.isCircularEmbroideryTestEnabled
                      }
                      blankDesign={this.props.blankDesign}
                      guidelines={guidelines}
                      handleOpenIconTools={this.props.handleOpenIconTools}
                      handleSelectIcon={this.props.handleSelectIcon}
                      handleSelectIcons={this.props.handleSelectIcons}
                      iconRendered={handleIconRender}
                      icons={this.props.icons}
                      interactive={this.props.interactive}
                      main={this.props.main}
                      move={this.props.move}
                      productColor={this.props.productColor}
                      productConstraint={constraint}
                      remove={this.props.remove}
                      repositionForTextEffects={
                        this.props.repositionForTextEffects
                      }
                      resize={this.props.resize}
                      scale={modifiedPixelScaleFactor}
                      selectedIconId={this.props.selectedIconId}
                      selectedIconIds={this.props.selectedIconIds}
                      selectedIconType={this.props.selectedIconType}
                      selectedViewId={this.props.selectedView.id}
                      selectedViewScaleFactor={
                        this.props.selectedView.printScaleFactor
                      }
                      updateFontSize={this.props.updateFontSize}
                      zoomConstraint={zoomConstraint}
                    />
                  </Product>

                  {this.props.interactive && this.renderContextmenu()}
                </div>
              )
            );
          }}
        </AutoSizer>
      </div>
    );
  }
}
